import React, { useState, useEffect } from "react";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import Sidebar from "../../../components/layout/Sidebar";
import Select from "react-select";
import { Row, Col, Button, Form, Table, Modal } from "react-bootstrap";
import axios from "axios";
import { BASE_URL } from "../../../env";
import { useAdminAuth } from "../../auth/AdminAuthProvider";
import CustomerDetailsModal from "../../members/components/CustomerDetailsModal";
import ApproveMisModal from "../../users-management/components/ApproveMisModal";
import RetriggerForeclosureModal from "../components/RetriggerForeclosureModal";
import SearchForm from "../components/Foreclosure/SearchForm";
import CustomerDetails from "../components/Foreclosure/CustomerDetails";
import DenominationTable from "../components/Foreclosure/DenominationTable";
import PendingApprovalsTable from "../components/Foreclosure/PendingApprovalsTable";
import ReceiptForm from "../components/Foreclosure/ReceiptForm";
import UnclearedReceiptsAlert from "../components/Foreclosure/UnclearedReceiptsAlert";

const Foreclosure = () => {
  const { adminUser } = useAdminAuth();
  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [searchType, setSearchType] = useState("prospectId");
  const [searchValue, setSearchValue] = useState("");
  const [customerDetails, setCustomerDetails] = useState(null);
  // Add new states for Account Manager view
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [viewMode, setViewMode] = useState(false);
  // Starts empty on purpose: the charge must be typed for every foreclosure,
  // never accepted from a pre-filled default.
  const [foreCloseCharges, setForeCloseCharges] = useState("");
  const [foreCloseChargesAmount, setForeCloseChargesAmount] = useState(0);
  const [foreCloseGST, setForeCloseGST] = useState(0);
  const [totalPayableAmount, setTotalPayableAmount] = useState(0);
  // Security deposit: deducted from the payable amount by default (toggle on),
  // per docs/business-rules.md §1.7: Net payable = Total payable - deposit.
  // securityDeposit is what the Branch Manager types (required, 0 allowed);
  // recordedSecurityDeposit is the loan's deposit in the database, shown for reference.
  const [securityDeposit, setSecurityDeposit] = useState("");
  const [recordedSecurityDeposit, setRecordedSecurityDeposit] = useState(null);
  const [isSecurityDepositAdjusted, setIsSecurityDepositAdjusted] = useState(true);
  const netPayableAmount = Math.max(
    0,
    Number(totalPayableAmount || 0) - (isSecurityDepositAdjusted ? parseInt(securityDeposit, 10) || 0 : 0)
  );
  const [reason, setReason] = useState("");
  const [waiverReason, setWaiverReason] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [showApproveMisModal, setShowApproveMisModal] = useState(false);
  const [approvalId, setApprovalId] = useState("");
  const [message, setMessage] = useState("");
  const [comment, setComment] = useState("");
  const [retriggerToRole, setRetriggerToRole] = useState("");
  const [showRetriggerModal, setShowRetriggerModal] = useState(false);
  const [memberData, setMemberData] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);

  // Denomination state
  const [denominations, setDenominations] = useState({
    500: 0,
    200: 0,
    100: 0,
    50: 0,
    20: 0,
    10: 0,
    Coins: 0,
  });

  const [denominationTotal, setDenominationTotal] = useState(0);

  // Cash collected on a saved foreclosure: the stored collectedAmount, else the
  // sum of the saved notes. Never totalPayableAmount, which is what was owed -
  // the two differ whenever a deposit was deducted or a waiver given.
  const savedCashTotal = (record, denominationObj) => {
    if (record.collectedAmount != null) return Number(record.collectedAmount) || 0;
    return Object.entries(denominationObj).reduce(
      (sum, [denom, count]) => sum + (denom === "Coins" ? Number(count) : Number(denom) * Number(count)),
      0
    );
  };

  // Function to get current IST datetime in proper MySQL format
  const getCurrentISTDateTime = () => {
    const now = new Date();
    // Add 5.5 hours to UTC to get IST
    const istTime = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
    // Format as YYYY-MM-DD HH:mm:ss for MySQL
    return istTime.toISOString().slice(0, 19).replace('T', ' ');
  };

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  // Handle search type change
  const handleSearchTypeChange = (e) => {
    setSearchType(e.target.value);
  };

  // Handle search value change
  const handleSearchValueChange = (e) => {
    setSearchValue(e.target.value);
  };

  // Handle search submission
  // Add near other state declarations at the top
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Update handleSearch function
  const handleSearch = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true); // Start loading
      setErrorMessage("");
      setShowReceipt(false);
      setMemberData(null);
      // Reset all states before new search
      setCustomerDetails(null);
      setForeCloseCharges("");
      setForeCloseChargesAmount(0);
      setForeCloseGST(0);
      setTotalPayableAmount(0);
      setSecurityDeposit("");
      setRecordedSecurityDeposit(null);
      setIsSecurityDepositAdjusted(true);
      setReason("");
      setWaiverReason("");
      setDenominations({
        500: 0,
        200: 0,
        100: 0,
        50: 0,
        20: 0,
        10: 0,
        Coins: 0,
      });
      setDenominationTotal(0);

      const response = await axios.get(
        `${BASE_URL}/api/getForeclosureDataForBm`,
        {
          params: {
            searchType,
            searchValue,
            role: adminUser.userType,
            manager_id: adminUser.user.id,
          },
        }
      );
      if (response.data.success) {
        if (response.data.data) {
          // Every receipt must be paid and fully approved before foreclosure;
          // until then the screen is read-only and lists the receipts to clear.
          const unclearedReceipts = response.data.data.unclearedReceipts || [];
          setCustomerDetails({
            ...response.data.data,
            unclearedReceipts,
            isReadOnly: unclearedReceipts.length > 0,
          });
          // The box starts empty: the Branch Manager enters the deposit actually
          // collected. The database figure is shown beside the label for reference.
          setSecurityDeposit("");
          setRecordedSecurityDeposit(Number(response.data.data.securityDeposit) || 0);
          setIsSecurityDepositAdjusted(true);
          calculateAmounts(response.data.data.outstandingPrincipal, "");
        } else if (response.data.foreclosureData) {
          // Handle existing foreclosure data
          const foreclosureData = response.data.foreclosureData;
          setCustomerDetails({
            memberId: foreclosureData.memberId,
            memberName: response.data.memberName,
            outstandingPrincipal: foreclosureData.totalOutstandingAmount,
            isReadOnly: foreclosureData.branchManagerStatus === "submitted", // Set readonly based on status
            approvalStatus: "Approval pending from Account Manager",
            branchManagerStatus: foreclosureData.branchManagerStatus,
            approvalId: foreclosureData.id,
          });
          setForeCloseCharges(foreclosureData.forecloseChargesPercentage);
          setForeCloseChargesAmount(foreclosureData.forecloseChargesAmount);
          setForeCloseGST(foreclosureData.forecloseGstAmount);
          setTotalPayableAmount(foreclosureData.totalPayableAmount);
          // Older foreclosures have no deposit recorded (NULL): shown as not adjusted.
          setSecurityDeposit(foreclosureData.securityDepositAmount ?? "");
          setIsSecurityDepositAdjusted(Boolean(foreclosureData.isSecurityDepositAdjusted));
          setReason(foreclosureData.reason);
          setWaiverReason(foreclosureData.waiverReason || "");

          // Convert denomination array to object format
          const denominationObj = {
            500: 0,
            200: 0,
            100: 0,
            50: 0,
            20: 0,
            10: 0,
            Coins: 0,
          };

          foreclosureData.fk_foreclosure_approval_hasMany_foreclosure_denominations_foreclosureId.forEach(
            (item) => {
              if (item.denomination === 1) {
                denominationObj.Coins = item.count;
              } else {
                denominationObj[item.denomination] = item.count;
              }
            }
          );

          setDenominations(denominationObj);
          setDenominationTotal(savedCashTotal(foreclosureData, denominationObj));
        } else if (response.data.receiptData) {
          setShowReceipt(true); // Add this line
          const receiptData = response.data.receiptData;
          const member = response.data.member;
          setCustomerDetails({
            memberId: receiptData.memberId,
            memberName: member.memberName,
            outstandingPrincipal: receiptData.totalOutstandingAmount,
            isReadOnly: receiptData.branchManagerStatus === "submitted", // Set readonly based on status
            approvalStatus: "Foreclosure approved by Account Manager",
            branchManagerStatus: receiptData.branchManagerStatus,
            approvalId: receiptData.id,
            branch: response.data.branchName,
            closedOn: receiptData.accountManagerStatusUpdatedAt,
          });
          setForeCloseCharges(receiptData.forecloseChargesPercentage);
          setForeCloseChargesAmount(receiptData.forecloseChargesAmount);
          setForeCloseGST(receiptData.forecloseGstAmount);
          setTotalPayableAmount(receiptData.totalPayableAmount);
          setSecurityDeposit(receiptData.securityDepositAmount ?? "");
          setIsSecurityDepositAdjusted(Boolean(receiptData.isSecurityDepositAdjusted));
          setReason(receiptData.reason);
          setWaiverReason(receiptData.waiverReason || "");
          setMemberData(member);

          // Convert denomination array to object format
          const denominationObj = {
            500: 0,
            200: 0,
            100: 0,
            50: 0,
            20: 0,
            10: 0,
            Coins: 0,
          };

          receiptData.fk_foreclosure_approval_hasMany_foreclosure_denominations_foreclosureId.forEach(
            (item) => {
              if (item.denomination === 1) {
                denominationObj.Coins = item.count;
              } else {
                denominationObj[item.denomination] = item.count;
              }
            }
          );

          setDenominations(denominationObj);
          setDenominationTotal(savedCashTotal(receiptData, denominationObj));
        }
      } else {
        setShowReceipt(false); // Add this line
        setCustomerDetails(null);
        setErrorMessage(
          response.data.error || "Failed to fetch customer details"
        );
      }
    } catch (error) {
      console.error("Error fetching customer details:", error);
      setErrorMessage(
        error.response?.data?.error ||
        "An error occurred while fetching customer details"
      );
    } finally {
      setIsLoading(false); // End loading
    }
  };

  // Handle foreclosure charges change
  const handleForeCloseChargesChange = (e) => {
    // Keep what was typed, so a cleared box stays empty instead of showing 0.
    const charges = e.target.value;
    setForeCloseCharges(charges);

    if (customerDetails) {
      calculateAmounts(customerDetails.outstandingPrincipal, charges);
    }
  };

  // Calculate all amounts based on outstanding amount and charges percentage
  const calculateAmounts = (outstandingAmount, charges = foreCloseCharges) => {
    const chargesAmount = Math.round((outstandingAmount * (parseFloat(charges) || 0)) / 100);
    const gstAmount = Math.round((chargesAmount * 18) / 100);
    const total = Math.round(outstandingAmount + chargesAmount + gstAmount);

    setForeCloseChargesAmount(chargesAmount);
    setForeCloseGST(gstAmount);
    setTotalPayableAmount(total);
  };

  // Handle denomination change
  const handleDenominationChange = (denomination, count) => {
    // Floor at 0. A note count cannot be negative, and parseInt("-5") would
    // otherwise pass straight through and make the collected total negative.
    // Guarding here covers typing, pasting and any other path into this handler.
    const newCount = Math.max(0, parseInt(count) || 0);
    const newDenominations = { ...denominations, [denomination]: newCount };
    setDenominations(newDenominations);

    // Calculate total
    let total = 0;
    Object.entries(newDenominations).forEach(([denom, count]) => {
      if (denom !== "Coins") {
        total += parseInt(denom) * count;
      } else {
        total += count; // For coins, just add the value directly
      }
    });

    setDenominationTotal(total);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Validate foreclosure charges
      // Required, but 0 is allowed (the screen only warns about 0 or above 20%).
      // 999.99 is the most the DECIMAL(5,2) column can hold.
      const chargesValue = parseFloat(foreCloseCharges);
      if (String(foreCloseCharges ?? "").trim() === "" || !Number.isFinite(chargesValue)) {
        alert("Please enter Foreclosure Charges (%)");
        return;
      }
      if (chargesValue < 0 || chargesValue > 999.99) {
        alert("Foreclosure Charges (%) must be between 0 and 999.99");
        return;
      }
      // Security deposit collected: required, whole rupees, 0 allowed.
      if (String(securityDeposit ?? "").trim() === "" || !/^\d+$/.test(String(securityDeposit).trim())) {
        alert("Please enter the Security Deposit amount collected (enter 0 if none)");
        return;
      }
      // A settlement may be negotiated BELOW the payable amount - the shortfall is
      // a waiver (write-off), not an error. Paying MORE than payable is still
      // rejected: that is an overpayment, a different case, and treating it as a
      // negative waiver would corrupt the figures.
      if (denominationTotal > netPayableAmount) {
        alert(
          "Collected amount cannot exceed the net payable amount. Please check the denominations."
        );
        return;
      }

      // Waiver = Net payable (after the security deposit) - cash collected.
      const waiverAmount = Math.max(0, netPayableAmount - denominationTotal);

      // A write-off must always carry a reason - it is the only record of why the
      // business gave up money it was owed.
      if (waiverAmount > 0 && !String(waiverReason || "").trim()) {
        alert(
          "A waiver reason is required when the settlement is less than the total payable amount."
        );
        return;
      }

      // Prepare data for submission
      const foreClosureData = {
        memberId: customerDetails?.memberId,
        memberName: customerDetails?.memberName,
        outstandingPrincipal: customerDetails?.outstandingPrincipal,
        foreCloseCharges: parseFloat(foreCloseCharges),
        foreCloseChargesAmount,
        foreCloseGST,
        totalPayableAmount,
        collectedAmount: denominationTotal,
        waiverAmount,
        waiverReason: waiverAmount > 0 ? waiverReason : null,
        securityDepositAmount: parseInt(securityDeposit, 10),
        isSecurityDepositAdjusted,
        reason,
        denominations,
        role: adminUser.userType,
        type:
          customerDetails?.branchManagerStatus === "retrigger" ? "Edit" : "Add",
        approvalId:
          customerDetails?.branchManagerStatus === "retrigger"
            ? customerDetails?.approvalId
            : undefined,
      };

      const response = await axios.post(
        `${BASE_URL}/api/foreclosureSubmit`,
        foreClosureData
      );
      if (response.data.message) {
        console.log("Foreclosure data submitted:", foreClosureData);
        alert("Foreclosure details submitted successfully!");
      }

      // Reset form
      setSearchValue("");
      setCustomerDetails(null);
      setForeCloseCharges("");
      setForeCloseChargesAmount(0);
      setForeCloseGST(0);
      setTotalPayableAmount(0);
      setSecurityDeposit("");
      setRecordedSecurityDeposit(null);
      setIsSecurityDepositAdjusted(true);
      setReason("");
      setWaiverReason("");
      setDenominations({
        500: 0,
        200: 0,
        100: 0,
        50: 0,
        20: 0,
        10: 0,
        Coins: 0,
      });
      setDenominationTotal(0);
    } catch (error) {
      console.error("Error submitting foreclosure data:", error);
      alert("Failed to submit foreclosure data. Please try again.");
    }
  };

  // Add useEffect to fetch pending approvals for Account Manager
  useEffect(() => {
    if (adminUser.userType === "Accounts Manager") {
      fetchPendingApprovals();
    }
  }, [adminUser.userType]);

  const fetchPendingApprovals = async () => {
    try {
      setIsLoading(true); // Start loading
      const response = await axios.get(
        `${BASE_URL}/api/getForeclosureDataForBm`,
        {
          params: { role: adminUser.userType },
        }
      );
      if (response.data.success) {
        setPendingApprovals(response.data.pendingForeclosures);
      }
    } catch (error) {
      console.error("Error fetching pending approvals:", error);
    } finally {
      setIsLoading(false); // End loading
    }
  };

  // Add handlers for approve and retrigger
  const handleApprove = async (id, msg, modalName) => {
    setApprovalId(id);
    setMessage(msg);
    if (modalName === "submitAm") {
      setShowApproveMisModal(true);
    } else if (modalName === "retrigger") {
      setShowRetriggerModal(true);
    }
  };

  const handleApproveConfirmAm = async () => {
    try {
      const currentISTDateTime = getCurrentISTDateTime(); // Use the fixed function
      const payload = {};
      payload.id = approvalId;
      payload.model = "foreclosure_approval";
      payload.type = "Edit";
      const formattedMessage =
        "\n" +
        comment +
        "\n---By " +
        adminUser.userType +
        "\n-- " +
        currentISTDateTime +
        "\n******** ";
      if (adminUser.userType === "Accounts Manager") {
        payload.accountManagerStatus = "submitted";
        payload.accountManagerMessage = formattedMessage;
        payload.accountManagerStatusUpdatedAt = currentISTDateTime;
        payload.loanCloseDate = currentISTDateTime;
      }

      await axios.post(`${BASE_URL}/api/addDetails`, {
        ...payload,
      });
      fetchPendingApprovals();
    } catch (error) {
      console.log("Error fetching list:", error);
    } finally {
      setShowApproveMisModal(false);
    }
  };

  const handleCloseApproveAm = async () => {
    setShowApproveMisModal(false);
  };

  const handleRetriggerConfirm = async () => {
    try {
      const currentISTDateTime = getCurrentISTDateTime(); // Use the fixed function
      const payload = {};
      payload.id = approvalId;
      payload.model = "foreclosure_approval";
      payload.type = "Edit";
      const formattedMessage =
        "\n" +
        comment +
        "\n---By " +
        adminUser.userType +
        "\n-- " +
        currentISTDateTime +
        "\n******** ";
      if (retriggerToRole === "Branch Manager") {
        payload.branchManagerStatus = "retrigger";
        payload.branchManagerMessage = formattedMessage;
        payload.branchManagerStatusUpdatedAt = currentISTDateTime;
      }

      await axios.post(`${BASE_URL}/api/addDetails`, {
        ...payload,
      });
      fetchPendingApprovals();
    } catch (error) {
      console.log("Error fetching list:", error);
    } finally {
      setShowRetriggerModal(false);
    }
  };

  const handleCloseRetrigger = async () => {
    setShowRetriggerModal(false);
  };

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section inline-header">
            <h1 className="TableHeaderText m-0">Foreclosure</h1>
            {adminUser.userType === "Branch Manager" && (
              <SearchForm
                searchType={searchType}
                searchValue={searchValue}
                handleSearchTypeChange={handleSearchTypeChange}
                handleSearchValueChange={handleSearchValueChange}
                handleSearch={handleSearch}
              />
            )}
          </div>

          <div
            className="master-list-section"
            style={{ padding: "20px", overflowY: "auto" }}
          >
            {isLoading && (
              <div className="text-center mb-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}

            {adminUser.userType === "Accounts Manager" && (
              <PendingApprovalsTable
                pendingApprovals={pendingApprovals}
                handleView={(approval) => {
                  const foreclosureData = approval;
                  const denominationObj = {
                    500: 0,
                    200: 0,
                    100: 0,
                    50: 0,
                    20: 0,
                    10: 0,
                    Coins: 0,
                  };

                  let collectedTotal = 0;
                  if (
                    foreclosureData.fk_foreclosure_approval_hasMany_foreclosure_denominations_foreclosureId
                  ) {
                    foreclosureData.fk_foreclosure_approval_hasMany_foreclosure_denominations_foreclosureId.forEach(
                      (item) => {
                        if (item.denomination === 1) {
                          denominationObj.Coins = item.count;
                          collectedTotal += Number(item.count);
                        } else {
                          denominationObj[item.denomination] = item.count;
                          collectedTotal += Number(item.denomination) * Number(item.count);
                        }
                      }
                    );
                  }

                  const computedWaiver = foreclosureData.waiverAmount !== undefined && foreclosureData.waiverAmount !== null
                    ? Number(foreclosureData.waiverAmount)
                    : Math.max(0, Number(foreclosureData.totalPayableAmount || 0) - collectedTotal);

                  setModalContent({
                    memberName:
                      foreclosureData
                        .fk_foreclosure_approval_belongsTo_member_details_memberId
                        ?.memberName,
                    totalOutstandingAmount:
                      foreclosureData.totalOutstandingAmount,
                    forecloseChargesPercentage:
                      foreclosureData.forecloseChargesPercentage,
                    forecloseChargesAmount:
                      foreclosureData.forecloseChargesAmount,
                    forecloseGstAmount: foreclosureData.forecloseGstAmount,
                    totalPayableAmount: foreclosureData.totalPayableAmount,
                    securityDepositAmount: foreclosureData.securityDepositAmount,
                    isSecurityDepositAdjusted: foreclosureData.isSecurityDepositAdjusted,
                    collectedAmount: collectedTotal > 0 ? collectedTotal : (foreclosureData.collectedAmount ?? (Number(foreclosureData.totalPayableAmount || 0) - computedWaiver)),
                    waiverAmount: computedWaiver,
                    reason: foreclosureData.reason,
                    waiverReason: foreclosureData.waiverReason || foreclosureData.reason,
                    denominations: denominationObj,
                    submittedDate: new Date(
                      foreclosureData.createdAt
                    ).toLocaleDateString("en-US"),
                  });
                  setShowModal(true);
                }}
                handleApprove={handleApprove}
              />
            )}

            {errorMessage && (
              <div className="alert alert-danger mb-3" role="alert">
                {errorMessage}
              </div>
            )}

            {customerDetails && (
              <Form className="foreclosure-wrapper">
                {showReceipt ? (
                  <ReceiptForm
                    customerDetails={customerDetails}
                    memberData={memberData}
                    denominations={denominations}
                    foreCloseCharges={foreCloseCharges}
                    foreCloseChargesAmount={foreCloseChargesAmount}
                    foreCloseGST={foreCloseGST}
                    totalPayableAmount={totalPayableAmount}
                    securityDeposit={securityDeposit}
                    isSecurityDepositAdjusted={isSecurityDepositAdjusted}
                    netPayableAmount={netPayableAmount}
                    collectedAmount={denominationTotal}
                    waiverAmount={Math.max(0, netPayableAmount - denominationTotal)}
                  />
                ) : (
                  <>
                    {customerDetails.unclearedReceipts?.length > 0 && (
                      <UnclearedReceiptsAlert receipts={customerDetails.unclearedReceipts} />
                    )}
                    <div className="foreclosure-grid mb-4">
                      <CustomerDetails
                        customerDetails={customerDetails}
                        foreCloseCharges={foreCloseCharges}
                        handleForeCloseChargesChange={
                          handleForeCloseChargesChange
                        }
                        foreCloseChargesAmount={foreCloseChargesAmount}
                        foreCloseGST={foreCloseGST}
                        totalPayableAmount={totalPayableAmount}
                        securityDeposit={securityDeposit}
                        setSecurityDeposit={setSecurityDeposit}
                        recordedSecurityDeposit={recordedSecurityDeposit}
                        isSecurityDepositAdjusted={isSecurityDepositAdjusted}
                        setIsSecurityDepositAdjusted={setIsSecurityDepositAdjusted}
                        netPayableAmount={netPayableAmount}
                        reason={reason}
                        setReason={setReason}
                        waiverReason={waiverReason}
                        setWaiverReason={setWaiverReason}
                        waiverAmount={Math.max(0, netPayableAmount - denominationTotal)}
                      />

                      <DenominationTable
                        denominations={denominations}
                        handleDenominationChange={handleDenominationChange}
                        denominationTotal={denominationTotal}
                        isReadOnly={customerDetails.isReadOnly}
                        totalPayableAmount={netPayableAmount}
                      />
                    </div>

                    <div className="text-center mb-4">
                      {!customerDetails.isReadOnly && (
                        <button className="btn btn-addCredential px-5 py-2" onClick={handleSubmit}>
                          {customerDetails.branchManagerStatus === "retrigger"
                            ? "Update Foreclosure"
                            : "Submit Foreclosure"}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </Form>
            )}
          </div>
        </div>
      </div>

      <CustomerDetailsModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        content={modalContent}
      />
      {showApproveMisModal && (
        <ApproveMisModal
          show={showApproveMisModal}
          handleClose={handleCloseApproveAm}
          handleConfirm={handleApproveConfirmAm}
          message={message}
          comment={comment}
          setComment={setComment}
        />
      )}
      {showRetriggerModal && (
        <RetriggerForeclosureModal
          show={showRetriggerModal}
          handleClose={handleCloseRetrigger}
          handleConfirm={handleRetriggerConfirm}
          message={message}
          comment={comment}
          setComment={setComment}
          retriggerToRole={retriggerToRole}
          setRetriggerToRole={setRetriggerToRole}
          roleId={adminUser.userType}
          item={approvalId}
        />
      )}
    </section>
  );
};

export default Foreclosure;